import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { ContentBox } from '@/common/layout/ContentBox.jsx';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

const MAX_PERIODS = 4;

export const GpoCustView = () => {
  const { navState, navigateTo } = useNavState();
  const rsrc = useRsrc();

  const [periodOptions, setPeriodOptions] = useState([]);
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const [fieldRows, setFieldRows] = useState([]);
  const [parentByRef, setParentByRef] = useState({});
  const [children, setChildren] = useState([]);
  const [showChildren, setShowChildren] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const [contextMenu, setContextMenu] = useState(null);

  const ident1 = navState?.ident1;
  const gpo = navState?.gpo;
  const initialPeriodId = navState?.periodId;

  const loadData = (periodIds) => {
    if (!ident1 || !gpo) {
      setPeriodOptions([]);
      setSelectedPeriods([]);
      setFieldRows([]);
      setParentByRef({});
      setChildren([]);
      return;
    }

    const reqParam = { ident1, gpo };
    if (periodIds) {
      reqParam.periodIds = periodIds;
    }

    rsrc.data('md', 'GPO_CUST_VIEW_DATA_PLUGIN', reqParam).then((res) => {
      const options = res?.periodOptions ?? [];
      const selected = res?.selectedPeriods ?? [];
      setPeriodOptions(options);
      setSelectedPeriods(selected);
      setFieldRows(res?.fieldRows ?? []);
      setParentByRef(res?.parentByRef ?? {});
      setChildren(res?.children ?? []);
    });
  };

  useEffect(() => {
    if (initialPeriodId) {
      loadData([initialPeriodId]);
    } else {
      loadData();
    }
  }, [ident1, gpo, initialPeriodId]);

  const handlePeriodChange = (_, newSelected) => {
    if (newSelected.length > MAX_PERIODS) {
      setSelectionError(`You can compare up to ${MAX_PERIODS} periods.`);
      return;
    }

    setSelectionError('');
    setSelectedPeriods(newSelected);
    loadData(newSelected.map((p) => p.value));
  };

  const openParentContextMenu = (event, parentRef, periodId) => {
    const parent = parentByRef[parentRef];
    if (!parent?.id) {
      return;
    }

    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      parent,
      periodId,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const navigateToParentHistory = () => {
    if (!contextMenu?.parent?.id) {
      closeContextMenu();
      return;
    }

    navigateTo('md/gpo-cust-view', {
      mode: Mode.DETAIL,
      entityId: contextMenu.parent.id,
      ident1: contextMenu.parent.ident1,
      gpo: contextMenu.parent.gpo,
      code: contextMenu.parent.code,
      name: contextMenu.parent.name,
      periodId: contextMenu.periodId,
    });
    closeContextMenu();
  };

  const navigateToChildHistory = (child) => {
    navigateTo('md/gpo-cust-view', {
      mode: Mode.DETAIL,
      entityId: child.id,
      ident1: child.ident1,
      gpo: child.gpo,
      code: child.code,
      name: child.name,
      periodId: child.periodId,
    });
  };

  return (
    <ContentBox>
      <Title
        icon={fas.faHistory}
        title="GPO Customer View"
        subtitle={navState?.name ? `${navState.name} (${navState?.code ?? ''})` : navState?.code}
        buttons={
          <Button variant="outlined" size="small" onClick={() => setShowChildren((prev) => !prev)}>
            {showChildren ? 'Hide Children' : `View Children (${children.length})`}
          </Button>
        }
      />

      {!ident1 || !gpo ?
        <Alert severity="warning">
          Missing customer context. Open this view from GPO Roster Dashboard row context menu.
        </Alert>
      : <>
          {!showChildren && (
            <>
              <Box sx={{ mb: 2, mt: 1, maxWidth: 720 }}>
                <Autocomplete
                  multiple
                  options={periodOptions}
                  getOptionLabel={(option) => option.displayName}
                  value={selectedPeriods}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  onChange={handlePeriodChange}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option.displayName} size="small" {...getTagProps({ index })} key={option.value} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Periods to Compare" helperText="Select up to 4 periods" />
                  )}
                />
                {selectionError && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {selectionError}
                  </Alert>
                )}
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 280px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 220 }}>Field</TableCell>
                      {selectedPeriods.map((period) => (
                        <TableCell key={period.value} sx={{ minWidth: 250 }}>
                          {period.displayName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fieldRows.map((row) => (
                      <TableRow key={row.field}>
                        <TableCell sx={{ fontWeight: 600 }}>{row.field}</TableCell>
                        {selectedPeriods.map((period, idx) => {
                          const value = row.values?.[idx] ?? '';
                          const prevValue = idx > 0 ? (row.values?.[idx - 1] ?? '') : '';
                          const changedFromPrior = idx > 0 && value !== prevValue;
                          const isParentRefRow = row.field === 'Parent Ref';
                          const isParentNavigable = isParentRefRow && !!value && !!parentByRef[value]?.id;

                          return (
                            <TableCell
                              key={`${row.field}-${period.value}`}
                              onContextMenu={
                                isParentNavigable ?
                                  (event) => openParentContextMenu(event, value, period.value)
                                : undefined
                              }
                              sx={{
                                ...(changedFromPrior ? { backgroundColor: 'action.hover', fontWeight: 600 } : {}),
                                ...(isParentNavigable ?
                                  {
                                    cursor: 'context-menu',
                                    textDecoration: 'underline dotted',
                                    textUnderlineOffset: '3px',
                                  }
                                : {}),
                              }}
                            >
                              {value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    {fieldRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={Math.max(2, selectedPeriods.length + 1)}>
                          <Typography variant="body2" color="text.secondary">
                            No comparison data found for the selected customer/periods.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {showChildren && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Related Children
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>GPO</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>GPO ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>HIN</TableCell>
                      <TableCell>DEA</TableCell>
                      <TableCell>Parent ID</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {children.map((child) => (
                      <TableRow key={`${child.id}-${child.ident1}`} hover>
                        <TableCell>{child.gpo}</TableCell>
                        <TableCell>{child.type}</TableCell>
                        <TableCell>{child.ident1}</TableCell>
                        <TableCell>{child.name}</TableCell>
                        <TableCell>{child.ident2}</TableCell>
                        <TableCell>{child.ident3}</TableCell>
                        <TableCell>{child.parentId}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => navigateToChildHistory(child)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {children.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary">
                            No children found for this parent.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Menu
            open={contextMenu !== null}
            onClose={closeContextMenu}
            anchorReference="anchorPosition"
            anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
          >
            <MenuItem onClick={navigateToParentHistory}>View History</MenuItem>
          </Menu>
        </>
      }
    </ContentBox>
  );
};
